// @erp/module-cms/schemas — the media-library wire contracts. Browser-safe: zod +
// plain data only (fe forms and the gateway parse the SAME schemas, so the admin UI
// and the server can never disagree on a valid upload). A FAITHFUL MIRROR of
// modules/lib/src/schemas/digital.ts §presigned uploads — the presign → client PUT →
// HEAD-verify complete → serve pipeline.
//
// Contract law (pinned, the LIB L5 precedent):
//   · PRESIGN, NEVER PROXY (the target R2 law, ADR Fig 10.7 / INFRA-04): the Worker
//     authorizes and records; the browser moves the bytes. Declared size/content-type
//     are validated HERE (no server ever holds the bytes to re-check); the complete
//     step's HEAD verify holds the client to this declaration.
//   · The r2_key is SERVER-MINTED + entity-scoped (media/<kind>/<ulid>.<ext>) and the
//     extension is derived FROM the validated MIME — the client filename is stored for
//     display only, NEVER trusted for the key (the MBSPSU derive-ext-from-MIME law).
//   · The MIME allowlist is a sensible CMS starter set — images (png/jpeg/webp/avif/
//     gif) + documents (pdf). image/svg+xml is deliberately EXCLUDED (script-bearing —
//     a content-injection vector on the R2 origin; the LIB IMAGE_CONTENT_TYPE_DENY law).
import { z } from "zod";
import { CursorQuery, EntityId, EpochMs } from "@erp/core-schemas";

/* ────────────────────────────── vocabularies ────────────────────────────── */

/** kind is DERIVED from the MIME class (image/* → 'image'; application/pdf → 'document'). */
export const MEDIA_KINDS = ["image", "document"] as const;
export const MediaKind = z.enum(MEDIA_KINDS);
export type MediaKind = z.infer<typeof MediaKind>;

/** public assets serve to anyone (the static site reads them); private demands an authorized caller. */
export const MEDIA_VISIBILITIES = ["public", "private"] as const;
export const MediaVisibility = z.enum(MEDIA_VISIBILITIES);
export type MediaVisibility = z.infer<typeof MediaVisibility>;

/* ─────────────────────── the MIME allowlist (kind + ext) ─────────────────── */
//
// One table drives THREE derivations (allow-check, server-minted extension, kind):
// the client sends a content_type; the server never trusts the filename's extension.

export interface MediaMimeSpec {
  ext: string;
  kind: MediaKind;
}

/** The allowlisted content types → their safe extension + kind (the MBSPSU MIME_TO_EXT precedent). */
export const MEDIA_MIME: Record<string, MediaMimeSpec> = {
  "image/png": { ext: "png", kind: "image" },
  "image/jpeg": { ext: "jpg", kind: "image" },
  "image/webp": { ext: "webp", kind: "image" },
  "image/avif": { ext: "avif", kind: "image" },
  "image/gif": { ext: "gif", kind: "image" },
  "application/pdf": { ext: "pdf", kind: "document" },
};

/** The allowlisted content types (runtime) — the presign body's enum + the service re-check. */
export const MEDIA_CONTENT_TYPES = Object.keys(MEDIA_MIME) as [string, ...string[]];

/** Shared predicate — the schema refine and the service re-check both call it (the cellKey two-layer law). */
export const mediaTypeAllowed = (contentType: string): boolean =>
  Object.hasOwn(MEDIA_MIME, contentType);

/** hasOwn-guarded lookup — an off-allowlist type throws (a programmer error; every caller pre-checks mediaTypeAllowed). */
const mimeSpec = (contentType: string): MediaMimeSpec => {
  if (!Object.hasOwn(MEDIA_MIME, contentType)) throw new Error(`content type not on the media allowlist: ${contentType}`);
  return MEDIA_MIME[contentType] as MediaMimeSpec;
};

/** The kind for an allowlisted content type (caller has already passed mediaTypeAllowed). */
export const mediaKindOf = (contentType: string): MediaKind => mimeSpec(contentType).kind;

/** The safe extension for an allowlisted content type — NEVER the client filename's. */
export const mediaExtOf = (contentType: string): string => mimeSpec(contentType).ext;

/* ─────────────────────────── caps + key + lease ─────────────────────────── */

/** Per-kind size caps — images 15 MB, documents 50 MB (a sensible CMS starter bound). */
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024;
export const MAX_DOCUMENT_BYTES = 50 * 1024 * 1024;
export const MEDIA_SIZE_CAPS: Record<MediaKind, number> = {
  image: MAX_IMAGE_BYTES,
  document: MAX_DOCUMENT_BYTES,
};
/** The overall ceiling the presign body's .max() enforces (the largest per-kind cap). */
export const MAX_MEDIA_BYTES = MAX_DOCUMENT_BYTES;

/** PUT lease: the pinned 10 minutes (ADR Fig 10.7). GETs stay short — minutes, never the 7-day max. */
export const PRESIGN_PUT_EXPIRES_S = 600;
export const PRESIGN_GET_EXPIRES_S = 300;

/** Server-minted key prefix per kind — <prefix>/<ulid>.<ext>; the DB stores the bare key, never a URL. */
export const MEDIA_KEY_PREFIXES: Record<MediaKind, string> = {
  image: "media/images",
  document: "media/documents",
};

/**
 * The server-minted, ENTITY-SCOPED key: media/<kind>/<ulid>.<ext>. Derived entirely
 * server-side — the ulid is the media entity's own id, the ext comes from the
 * validated MIME, and NOTHING from the client filename reaches the key. The ulid is
 * a bare ULID/[A-Za-z0-9_-] token, so the assembled key can carry no '/', '..',
 * NUL, or encoded separator (traversal-safe by construction; the MBSPSU guard's
 * job is done here at mint time, not after the fact).
 */
export const mediaKeyFor = (kind: MediaKind, id: string, ext: string): string =>
  `${MEDIA_KEY_PREFIXES[kind]}/${id}.${ext}`;

/* ─────────────────────────── presign (authorize) ────────────────────────── */

/**
 * POST /media/uploads — the authorize step. The declared filename is a DISPLAY
 * label (stored on the media row, never used for the key); content_type must be
 * allowlisted; size is bounded overall + per-kind. visibility rides here so the
 * completed row is born public/private without a second call.
 */
export const PresignMediaBody = z
  .object({
    /** The client's original filename — stored for display; NEVER trusted for the key or extension. */
    filename: z.string().trim().min(1).max(255),
    contentType: z.enum(MEDIA_CONTENT_TYPES),
    sizeBytes: z.number().int().min(1).max(MAX_MEDIA_BYTES),
    visibility: MediaVisibility.default("public"),
  })
  .refine((v) => mediaTypeAllowed(v.contentType), {
    message: "content type is not on the media allowlist",
    path: ["contentType"],
  })
  .refine((v) => v.sizeBytes <= MEDIA_SIZE_CAPS[mediaKindOf(v.contentType)], {
    message: "declared size exceeds the cap for this media kind",
    path: ["sizeBytes"],
  });
export type PresignMediaBody = z.infer<typeof PresignMediaBody>;

/** The 201: a pending ledger row + the one-shot PUT URL. expiresAt = created_at + PRESIGN_PUT_EXPIRES_S·1000. */
export const PresignMediaResult = z.object({
  uploadId: EntityId, // cms_upload.id (ULID)
  key: z.string().min(1), // bare R2 key (MEDIA_KEY_PREFIXES shape) — what cms_media.r2_key stores
  url: z.string().min(1), // presigned PUT — client↔R2 direct, bytes never transit the Worker
  expiresAt: EpochMs,
});
export type PresignMediaResult = z.infer<typeof PresignMediaResult>;

/* ─────────────────────────── complete (HEAD verify) ─────────────────────── */

/**
 * POST /media/uploads/:id/complete — the row is route-addressed. `etag`/`sizeBytes`
 * are OPTIONAL client echoes of the PUT response, cross-checked against the HEAD
 * truth (a contradiction → CMS_UPLOAD_MISMATCH; quotes/case on the etag normalize
 * on both sides). width/height/altText are display metadata R2's HEAD cannot know
 * (it returns size+etag only), so they ride the body and are stored as-is.
 */
export const CompleteMediaBody = z.object({
  etag: z.string().trim().min(1).max(128).optional(),
  sizeBytes: z.number().int().min(1).max(MAX_MEDIA_BYTES).optional(),
  width: z.number().int().min(0).max(100_000).optional(),
  height: z.number().int().min(0).max(100_000).optional(),
  altText: z.string().trim().max(1000).optional(),
});
export type CompleteMediaBody = z.infer<typeof CompleteMediaBody>;

/* ─────────────────────────────── the media DTO ──────────────────────────── */

/**
 * The media-library read shape (camelCase; the audit actor column stays server-side).
 * Metadata-only — NO inline signed URL: the presigned GET is minted ONLY by the
 * access-gated GET /media/:id serve (the LIB metadata-only-DTO law — a list must
 * never hand out signed URLs to the whole library). `key` is the bare r2_key the
 * serve route resolves.
 */
export const Media = z.object({
  id: EntityId,
  key: z.string().min(1),
  contentType: z.string(),
  sizeBytes: z.number().int().min(0),
  filename: z.string(),
  kind: z.string(), // open on read; writes ride MediaKind
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  altText: z.string().nullable(),
  visibility: z.string(), // open on read; writes ride MediaVisibility
  createdAt: EpochMs,
  updatedAt: EpochMs,
});
export type Media = z.infer<typeof Media>;

/**
 * GET /media/:id — the serve verdict: the media metadata + a short-lived presigned GET, plus
 * `publicUrl`: the STABLE address the object has on the public SITE once its record is
 * published (service.ts publicMediaUrl — `${CMS_PUBLIC_BASE_URL}/${r2_key}`; the dev serve
 * route locally; v1.2: the site serves it from the private bucket behind the media-keys
 * gate, nothing is copied). The admin attachment / banner editors store THIS in an `url`
 * field (a presigned link expires in minutes; static HTML needs an address that outlives
 * it). null when the deployment has no public base, the row is private, or the object is a
 * live private record's asset — the same objects the gate refuses (2026-09-13).
 */
export const MediaServe = Media.extend({
  url: z.string().min(1),
  expiresAt: EpochMs,
  publicUrl: z.string().nullable(),
});
export type MediaServe = z.infer<typeof MediaServe>;

/**
 * PATCH /media/:id — the two display facts an editor may change after upload (2026-09-13,
 * the admin-forms reconciliation): the alt text (nullable to clear) and the visibility.
 * Nothing else is editable — the key, type, size and dimensions are the object's own
 * truth (HEAD-verified at complete), and a "rename" is a new upload. Partial: an omitted
 * key keeps the stored value. Flipping a row to private does not un-copy an object the
 * reconciler already advertised — the next reconcile drops it from the desired set.
 */
export const UpdateMediaBody = z
  .object({
    altText: z.string().trim().max(1000).nullable(),
    visibility: MediaVisibility,
  })
  .partial();
export type UpdateMediaBody = z.infer<typeof UpdateMediaBody>;

/* ────────────────────────────── list query ──────────────────────────────── */

/** Keyset list, fixed ORDER BY (created_at DESC, id DESC); ?kind/?visibility exact-eq; ?search over filename. */
export const MediaListQuery = CursorQuery.extend({
  kind: MediaKind.optional(),
  visibility: MediaVisibility.optional(),
  search: z.string().trim().min(1).max(200).optional(),
});
export type MediaListQuery = z.infer<typeof MediaListQuery>;
